// Build-time staging flag.
//
// The staging build (next.pokerledger.club) is produced with
// REACT_APP_STAGING=true — see `make web/build-staging` and the
// staging-deploy CircleCI workflow. The production build cut from `main`
// leaves it unset, so anything gated on `isStaging` ships to staging while
// staying hidden in production. Use it to preview in-progress UI on `next`:
//
//   import { isStaging } from "../config/staging";
//   {isStaging && <NewFeatureInProgress />}
//
// See docs/adr/0001-dual-environment-staging-via-enable-staging-flag.md.
export const isStaging = process.env.REACT_APP_STAGING === "true";
export const isProduction = process.env.NODE_ENV === "production";

// Runtime production-host check.
//
// Build-time NODE_ENV can't distinguish a production *build* served locally
// (e.g. `make web/build` then serving ./build) — or the staging build — from
// the real production deployment; all are cut with NODE_ENV=production. For
// anything that should reflect the environment the app is actually *running
// in* (e.g. the favicon's environment indicator), key off the live hostname
// instead. Only the canonical production domain counts as production;
// localhost, IPs, preview hosts, and next.pokerledger.club all read as
// non-prod so the indicator shows everywhere except real production.
const PRODUCTION_HOSTS = new Set([
    "pokerledger.club",
    "app.pokerledger.club",
]);

export const isProductionHost =
    typeof window !== "undefined" &&
    PRODUCTION_HOSTS.has(window.location.hostname);

// Runtime staging-host check — the deployed staging surface. Parallel to
// isProductionHost so the favicon can give staging its own indicator
// distinct from local development.
const STAGING_HOSTS = new Set([
    "next.pokerledger.club",
]);

export const isStagingHost =
    typeof window !== "undefined" &&
    STAGING_HOSTS.has(window.location.hostname);

// Base URL of the marketing site (the www/app split). Defaults to the
// production marketing host; override REACT_APP_WWW_URL to change it — the
// staging build points it at next.pokerledger.club (Makefile) and local dev at
// the Astro dev server (web/.env.development). Marketing links (home, terms,
// privacy, how-it-works) navigate here; set it empty to force in-app routes.
export const WWW_URL =
    process.env.REACT_APP_WWW_URL ?? "https://www.pokerledger.club";

// Build an href to a marketing-site path: an absolute URL on the marketing host
// when WWW_URL is set, otherwise the app-relative path. Use for plain <a> links
// to marketing pages (home/terms/privacy); MarketingLink wraps this for the
// Link-vs-anchor cases.
export const wwwUrl = (path = "/") => `${WWW_URL}${path}`;

// Cloudflare Turnstile site key for the signup widget
// (docs/bot-signup-abuse.md). This is a PUBLIC value — it ships to every
// browser in the JS bundle — so it lives here as the production default,
// same as WWW_URL. (Only the paired *secret* is sensitive; that stays in
// SSM/Doppler.) Committing it means every build path, including a local
// `make app/deploy`, ships the widget with no extra env plumbing.
//
// Override with REACT_APP_TURNSTILE_SITE_KEY: the compose dev stack injects
// Cloudflare's always-pass test key, and web/.env.development sets it empty
// so plain `make web/run` hides the widget (the real key 600010s on
// localhost unless localhost is on the widget's hostname allowlist).
export const TURNSTILE_SITE_KEY =
    process.env.REACT_APP_TURNSTILE_SITE_KEY ?? "0x4AAAAAAD5QG76CVdK7ZcHJ";
